/**
 * Build a web maps URL for the user's preferred map app (iOS PreferredMapApp parity).
 * Uses https fallbacks suitable for browser (native schemes are not reliable on web).
 */

export type PreferredMapAppId = 'apple' | 'google' | 'waze';

export function normalizePreferredMapApp(
  value: string | null | undefined
): PreferredMapAppId {
  const v = (value || 'apple').trim().toLowerCase();
  if (v === 'google' || v === 'waze' || v === 'apple') return v;
  return 'apple';
}

export function mapsSearchUrl(input: {
  preferredMapApp?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  query?: string | null;
  name?: string | null;
}): string | null {
  const app = normalizePreferredMapApp(input.preferredMapApp);
  const lat = input.latitude;
  const lng = input.longitude;
  const hasCoords =
    lat != null &&
    lng != null &&
    Number.isFinite(lat) &&
    Number.isFinite(lng) &&
    !(lat === 0 && lng === 0);
  const label = (input.name || input.query || '').trim();
  const query = (input.query || input.name || '').trim();

  if (hasCoords) {
    const encodedName = encodeURIComponent(label || `${lat},${lng}`);
    switch (app) {
      case 'apple':
        return `https://maps.apple.com/?ll=${lat},${lng}&q=${encodedName}`;
      case 'waze':
        return `https://waze.com/ul?ll=${lat},${lng}&navigate=yes`;
      case 'google':
      default:
        return `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;
    }
  }

  if (!query) return null;
  const encoded = encodeURIComponent(query);
  switch (app) {
    case 'apple':
      return `https://maps.apple.com/?q=${encoded}`;
    case 'waze':
      return `https://waze.com/ul?q=${encoded}`;
    case 'google':
    default:
      return `https://www.google.com/maps/search/?api=1&query=${encoded}`;
  }
}
